import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { adminEndpoints } from "@/lib/adminApi";
import { AdminPageHeader, AdminCard, EmptyState } from "@/components/admin/AdminUI";

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

export default function AdminCertificatesPage() {
    const [certificates, setCertificates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        adminEndpoints
            .certificates()
            .then((d) => {
                if (!cancelled) setCertificates(d);
            })
            .catch((err) => {
                if (!cancelled)
                    setError(err?.response?.data?.detail || "Failed to load certificates");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const copyUrl = async (url) => {
        try {
            await navigator.clipboard.writeText(url);
            toast.success("Verification URL copied");
        } catch {
            toast.error("Could not copy — copy manually");
        }
    };

    return (
        <div data-testid="admin-certificates-page">
            <AdminPageHeader
                eyebrow="Verified issuance"
                title="Certificates"
                description="Every certificate issued by the training portal. Each row includes a copy-to-clipboard for its public verification URL."
                testId="admin-certificates-header"
            />
            {error && (
                <p className="mb-6 border border-rose-400/40 bg-rose-500/10 px-4 py-3 font-mono text-xs text-rose-200">
                    {error}
                </p>
            )}
            {loading ? (
                <AdminCard>Loading…</AdminCard>
            ) : certificates.length === 0 ? (
                <EmptyState title="No certificates issued yet" testId="admin-certificates-empty" />
            ) : (
                <div className="overflow-x-auto border border-white/10 bg-white/[0.02]">
                    <table className="min-w-full text-left text-sm" data-testid="admin-certificates-table">
                        <thead className="border-b border-white/10 bg-white/[0.03] font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/60">
                            <tr>
                                <th className="px-4 py-3">Holder</th>
                                <th className="px-4 py-3">Course</th>
                                <th className="px-4 py-3">Cert #</th>
                                <th className="px-4 py-3">Issued</th>
                                <th className="px-4 py-3">Verification</th>
                            </tr>
                        </thead>
                        <tbody>
                            {certificates.map((c) => (
                                <tr
                                    key={c.id}
                                    className="border-b border-white/5 last:border-0 hover:bg-white/[0.03]"
                                    data-testid={`admin-cert-row-${c.id}`}
                                >
                                    <td className="px-4 py-3 font-body text-brand-offwhite">{c.holder_name}</td>
                                    <td className="px-4 py-3 font-body text-brand-offwhite/80">
                                        {c.course_title}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-gold">
                                        {c.cert_number}
                                    </td>
                                    <td className="px-4 py-3 font-mono text-xs text-brand-offwhite/60">
                                        {formatDate(c.issued_at)}
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            type="button"
                                            onClick={() => copyUrl(c.verification_url)}
                                            className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite hover:border-brand-gold hover:text-brand-gold"
                                            data-testid={`admin-cert-copy-${c.id}`}
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                            Copy URL
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
